import {
  FunctionComponent,
  useCallback,
  memo,
  useState,
  SyntheticEvent,
} from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { useModal } from './hooks';
import { useConfig } from '~components/hooks';
import { useGetItems } from '../item/hooks';
import { useGetGame } from '../game/hooks';
import GameChange from '../game/GameChange';
import { SteamItemImage } from '../item';
import Currency from '../Currency';
import { Price } from '~ui/Price';
import { Loader } from '../Loader';
import CloseIcon from './closeIcon';
import TradeIcon from './IconTrade';

interface Props {
  ids: number[];
  gameId: number;
  onClick: () => void;
}

const WithdrawView: FunctionComponent<Props> = memo(
  ({ ids, gameId, onClick }) => {
    const { t } = useTranslation();
    const [replaceItem, setReplaceItem] = useState<number | null>(null);
    const getItems = useGetItems(ids);
    const getGame = useGetGame(gameId);
    const getConfig = useConfig();
    const count = _.size(getItems);

    const onSetReplaceItem = useCallback(
      (event: SyntheticEvent<HTMLElement>) => {
        const id = Number(event.currentTarget.dataset.id);
        setReplaceItem((prev) => (prev === id ? null : id));
      },
      [setReplaceItem],
    );

    const rarityMap = {
      b0c3d9: 'consumer',
      '4b69ff': 'military',
      '5e98d9': 'industrial',
      '8847ff': 'restricted',
      d32ce6: 'classified',
      eb4b4b: 'covert',
      ffc600: 'legendary',
      ffc700: 'legendary',
      ffd600: 'legendary',
      ffd700: 'legendary',
    };

    const mainRarity = getGame?.getItem?.color
      ? rarityMap[getGame.getItem.color] || 'default'
      : 'default';

    return (
      <div className="withdraw-none-items">
        <div className="withdraw-none-items-content">
          <div className="withdraw-none-items-head">
            <h2 className="title">{t('withdraw_none_title')}</h2>
            <div className="withdraw-none-items-close" onClick={onClick}>
              <CloseIcon />
            </div>
          </div>
          <div>
            {getGame ? (
              <div
                className={`withdraw-none-items-prize withdraw-none-items-prize_${mainRarity}`}
              >
                <div className="withdraw-none-items-prize-header">
                  <div className="withdraw-none-items-prize-price">
                    {getConfig.isEU ? (
                      <>
                        <Currency />
                        <Price sum={getGame?.price} />
                      </>
                    ) : (
                      <>
                        <Price sum={getGame?.price} />
                        <Currency />
                      </>
                    )}
                  </div>
                </div>
                <SteamItemImage
                  url={getGame?.getItem?.getImage}
                  alt="prize"
                  width={218}
                  height={164}
                  className="withdraw-none-items-prize-image"
                />
                <div className="withdraw-none-items-prize-info">
                  <div className="withdraw-none-items-prize-tag">
                    <span>{getGame?.getItem?.quality}</span> |{' '}
                    {getGame?.getItem?.getName.split('|')[0].trim()}
                  </div>
                  <div className="withdraw-none-items-prize-name">
                    {getGame?.getItem?.getName.split('|')[1].trim()}
                  </div>
                </div>
              </div>
            ) : (
              <Loader />
            )}
            <div className="withdraw-none-items-subtitle">
              {t('withdraw_none_text_0')} {count} {t('item', { count })}{' '}
              {t('withdraw_none_text_1')}
            </div>
            <div className="withdraw-none-items-available">
              {getItems ? (
                _.map(getItems, (getItem) => {
                  const itemRarity = getItem.color
                    ? rarityMap[getItem.color] || 'default'
                    : 'default';
                  return (
                    <div
                      key={getItem.id}
                      className={`withdraw-none-items-prize withdraw-none-items-prize_${itemRarity} ${
                        replaceItem === getItem.id ? 'selected' : ''
                      }`}
                      data-id={getItem.id}
                      onClick={onSetReplaceItem}
                    >
                      <div
                        className="withdraw-none-items-in-trade"
                        style={{
                          opacity: replaceItem === getItem.id ? 1 : 0,
                          pointerEvents:
                            replaceItem === getItem.id ? 'auto' : 'none',
                        }}
                      >
                        <TradeIcon />
                        {t('Trade')}
                      </div>
                      <div className="withdraw-none-items-prize-header">
                        <div className="withdraw-none-items-prize-price">
                          {getConfig.isEU ? (
                            <>
                              <Currency />
                              <Price sum={getItem.price} />
                            </>
                          ) : (
                            <>
                              <Price sum={getItem.price} />
                              <Currency />
                            </>
                          )}
                        </div>
                      </div>
                      <SteamItemImage
                        url={getItem?.getImage}
                        alt={getItem?.getName}
                        width={218}
                        height={164}
                        className="withdraw-none-items-prize-image"
                      />
                      <div className="withdraw-none-items-prize-info">
                        <div className="withdraw-none-items-prize-tag">
                          <span>{getItem?.quality}</span> |{' '}
                          {getItem?.getName.split('|')[0].trim()}
                        </div>
                        <div className="withdraw-none-items-prize-name">
                          {getItem?.getName.split('|')[1].trim()}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <Loader />
              )}
            </div>
            <div className="btns">
              <GameChange
                className={`btn btn1_dark withdraw-none-items-button ${
                  replaceItem === null ? 'disabled' : ''
                }`}
                type={1}
                getGame={getGame}
                onDone={onClick}
                replaceItem={replaceItem}
              >
                <span className="text">{t('send_trade')}</span>
              </GameChange>
              <GameChange
                className="btn btn1_green withdraw-none-items-button"
                type={0}
                getGame={getGame}
                onDone={onClick}
              >
                <span className="text">{t('Sell')}</span>
                <div className="cost">
                  {getConfig.isEU ? (
                    <>
                      <Currency />
                      <Price sum={getGame?.priceSell} />
                    </>
                  ) : (
                    <>
                      <Price sum={getGame?.priceSell} />
                      <Currency />
                    </>
                  )}
                </div>
              </GameChange>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export const WithdrawNone: FunctionComponent = () => {
  const modalName = 'WithdrawNone';
  const { onClose, isOpen, props } = useModal<Props>(modalName);

  const onClick = () => {
    onClose();
  };

  return (
    <Modal show={isOpen} onClose={onClick} width={732} modalName={modalName}>
      <WithdrawView {...props} onClick={onClick} />
    </Modal>
  );
};
